import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionTag from "@/components/SectionTag";
import CtaBand from "@/components/CtaBand";

const MILESTONES = [
  { year: "1991", text: "Began his medical career as a medic and EMT in the United States Air Force" },
  { year: "2002", text: "Earned his Registered Nurse degree" },
  { year: "2012", text: "Completed his Family Nurse Practitioner degree at Azusa Pacific University" },
  { year: "Today", text: "20+ years in acute and emergency care, and still actively practicing trauma care" },
];

export default function MeetJeremy() {
  return (
    <main data-testid="meet-jeremy-page">
      <Seo
        title="Meet Jeremy Pene, FNP-C | Advantage Primary Health, Prescott Valley"
        description="Meet Jeremy Pene, FNP-C—over 20 years of acute and emergency care experience, now providing personalized primary care in Prescott Valley, AZ."
      />

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-14 sm:px-6 md:pt-20 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto max-w-md"
            >
              <div className="absolute -inset-3 rotate-2 rounded-[2.5rem] bg-brand-redlight" />
              <div className="absolute -inset-3 -rotate-1 rounded-[2.5rem] border border-slate-200" />
              <img
                src="/images/jeremy-headshot.png"
                alt="Jeremy Pene, FNP-C"
                data-testid="jeremy-headshot"
                className="relative aspect-[4/5] w-full rounded-[2rem] object-cover shadow-lift"
              />
            </motion.div>
          </div>
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <SectionTag number="01" label="Your Provider" />
              <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
                Meet Jeremy Pene,{" "}
                <span className="font-serif italic font-medium text-brand-red">FNP-C</span>
              </h1>
              <p className="mt-7 text-base leading-relaxed text-slate-600 sm:text-lg">
                Jeremy brings more than 20 years of experience in acute and
                emergency care to Advantage Primary Health, where he is
                committed to delivering high-quality, personalized care to the
                Prescott Valley community. He continues to actively practice
                trauma care, helping keep his clinical skills current and
                impactful.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                At Advantage, Jeremy and his family are proud to be part of a
                military family-owned and operated practice built around strong
                patient relationships and compassionate care. Here, family is
                at the center of everything we do.
              </p>
              <Link
                to="/about"
                data-testid="jeremy-about-link"
                className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-red transition-colors duration-200 hover:text-brand-redhover"
              >
                Read the Advantage story
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/70">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-24 lg:px-12">
          <Reveal>
            <SectionTag number="02" label="The Journey" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">
              Three decades of{" "}
              <span className="font-serif italic text-brand-red">showing up</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.year} delay={i * 0.08}>
                <div
                  data-testid={`milestone-${i + 1}`}
                  className="h-full rounded-3xl border border-slate-100 bg-white p-7 shadow-soft transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift"
                >
                  <span className="font-mono text-sm tracking-[0.2em] text-brand-red">
                    {m.year}
                  </span>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Become a patient of Jeremy's."
        accent="of Jeremy's."
        sub="Book your first visit online, or call for a same-day appointment."
        testidPrefix="jeremy-cta"
      />
    </main>
  );
}
